import React, { useState } from 'react';
import { Tabs, Card, Select, Row, Spin, DatePicker, Col, Empty, Grid } from 'antd';
import { isEmpty, capitalize } from 'lodash';
import moment from 'moment-timezone';
import { useTimezone } from '../../../../../../core/hooks/useTimezone';
import DualAxisArea from './components/tabvisualization/charts/DualAxisArea';
import SingleAxisBarChart from './components/tabvisualization/charts/SingleAxisBarChart';
import Creatives from '../../components/creatives/Creatives';
// import LineItems from "../../components/LineItems";
import Publishers from '../../components/publishers/Publishers';
//import VideoLogs from "../../components/VideoLogs";
import SegmentsDetails from '../../components/segments/SegmentsDetails';
// Global Styles
import {
  MenuItemTextColor,
  TabText,
  TooltipText,
} from '../../../../../shared/globalStyling/styledText';
import VideoCompletionRate from '../../components/VideoCompletionRate';
import { isDayOfWeekString, isUnixTimestamp } from '../../../../../../core/utils/campaigns';
import LineItemsCard from './components/lineItems/LineItemsCard';
import CampaignsCard from './components/campaigns/CampaignsCard';

const { Option } = Select;
const { RangePicker } = DatePicker;
const Frag = React.Fragment;
const colStyle = {
  cssStyle: {
    backgroundColor: '#fff',
    padding: '10px',
  },
  histogram: {
    width: 'auto',
  },
  divider: { height: '6em' },
};
const orderedDays = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const OverviewTab = ({
  chartData,
  chartsLoading,
  selectedCampaign,
  hasInsights,
  byCampaignRangeDate,
  setByCampaignRangeDate,
  dataCampaignRangeDate,
  setDataCampaignRangeDate,
  selectedSegmentsDimension,
  setSegmentsDimension,
  userPermission,
  displayReportsConnection,
  handleSelectPastReport,
  handleAfterChangePastReport,
  pastReportIdx,
}) => {
  //TODO - DUAL AXIS CHART CONTROLLERS SHOULD BE BROKEN OUT OF THIS COMPONENT

  //State controllers for right and left axis toggle
  const [leftY, setLeftY] = useState('Impressions');
  const [rightY, setRightY] = useState('Clicks');

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();

  const aggregate = displayReportsConnection
    ? displayReportsConnection.campaignReportsConnection.aggregate
    : null;
  const edges = displayReportsConnection
    ? displayReportsConnection.campaignReportsConnection.edges
    : null;

  const sliderDateFormat = 'M/DD/YY HH:MM';
  const lastNodeCreatedAt = !isEmpty(edges)
    ? moment(edges[edges.length - 1].node.createdAt).format(sliderDateFormat)
    : null;
  const firstNodeCreatedAt = !isEmpty(edges)
    ? moment(edges[0].node.createdAt).format(sliderDateFormat)
    : null;

  let byDate,
    byDay,
    deviceType,
    chartsBuilt,
    creatives,
    publishers,
    videoLogs,
    segments,
    lineItems,
    campaigns,
    creativesWithDates,
    publishersWithDates;
  const { segment, isTrafficker } = userPermission;

  try {
    if (chartData) {
      byDate = chartData.byDate.map((date) => {
        return {
          ...date,
          xAxis: isUnixTimestamp(date.xAxis)
            ? moment.unix(date.xAxis).utc()
            : moment(new Date(date.xAxis)).utc(),
        };
      });
      byDay = chartData.byDay;

      if (!isEmpty(byDay)) {
        byDay = chartData.byDay.map((day) => {
          return {
            ...day,
            xAxis: isDayOfWeekString(day.xAxis) ? moment().isoWeekday(day.xAxis) : day.xAxis,
          };
        });
        byDay.sort((a, b) => {
          return orderedDays[a.xAxis] - orderedDays[b.xAxis];
        });
      }

      videoLogs =
        chartData.videoLogs &&
        chartData.videoLogs.filter(
          (log) =>
            log.video_firstquartile > 0 &&
            log.video_midpoint > 0 &&
            log.video_thirdquartile > 0 &&
            log.video_complete > 0
        );

      deviceType =
        chartData.deviceType &&
        chartData.deviceType.map((device) => {
          if (!Object.keys(device).includes('xAxis')) {
            device['xAxis'] = 'Unknown';
          }
          return device;
        });

      segments = chartData.segments;
      chartsBuilt = chartData.chartsBuilt;
      creatives = chartData.creatives;
      publishers = chartData.publishers;
      lineItems = chartData.lineItems;
      campaigns = chartData.campaigns;

      // Extract creativesWithDates and publishersWithDates from displayReports
      if (chartData.displayReports && chartData.displayReports.length > 0) {
        // displayReports is an array of [provider, reportData] pairs
        const allCreativesWithDates = [];
        const allPublishersWithDates = [];

        chartData.displayReports.forEach(([provider, reportData]) => {
          if (reportData.creativesWithDates) {
            allCreativesWithDates.push(...reportData.creativesWithDates);
          }
          if (reportData.publishersWithDates) {
            allPublishersWithDates.push(...reportData.publishersWithDates);
          }
        });

        creativesWithDates = allCreativesWithDates.length > 0 ? allCreativesWithDates : null;
        publishersWithDates = allPublishersWithDates.length > 0 ? allPublishersWithDates : null;
      } else {
        // Fallback to top-level if displayReports not available
        creativesWithDates = chartData.creativesWithDates;
        publishersWithDates = chartData.publishersWithDates;
      }
    }
  } catch (e) {
    console.log(e);
  }

  // Function to filter and aggregate data by date range
  const filterAndAggregateData = (data, startDate, endDate, dataType, shouldAggregate = true) => {
    if (!data || !Array.isArray(data) || !startDate || !endDate) {
      return data;
    }

    // Determine creatives activity
    let activeCreatives = new Set();
    if (dataType === 'creatives' && chartsBuilt) {
      const fiveDaysBeforeChartsBuilt = moment(chartsBuilt).subtract(5, 'days');
      const chartsBuiltMoment = moment(chartsBuilt);
      data.forEach((item) => {
        if (item.impressions > 0 && item.date) {
          const itemDate = moment(item.date);
          if (
            itemDate.isSameOrAfter(fiveDaysBeforeChartsBuilt) &&
            itemDate.isSameOrBefore(chartsBuiltMoment)
          ) {
            activeCreatives.add(item.creativeId);
          }
        }
      });
    }
    const filteredData = data.filter((item) => {
      if (!item.date) {
        return true;
      }

      const itemDate = moment(item.date);
      const isInRange =
        itemDate.isSameOrAfter(moment(startDate), 'day') &&
        itemDate.isSameOrBefore(moment(endDate), 'day');

      return isInRange;
    });

    // Aggregate data by creative/publisher to match original summary format
    const aggregatedData = {};

    filteredData.forEach((item) => {
      let key, displayName;

      if (dataType === 'creatives') {
        // For creatives, use creative name or ID as key and display name
        key = item.creativeId || item.creativeName || item.xAxis;
        displayName = item.creativeName || item.creativeId || item.xAxis || `Creative ${key}`;
      } else {
        // For publishers, use publisher name or identifier as key and display name
        key = item.publisherName || item.publisherIdentifier || item.xAxis;
        displayName =
          item.publisherName || item.publisherIdentifier || item.xAxis || `Publisher ${key}`;
      }

      if (!aggregatedData[key]) {
        aggregatedData[key] = {
          xAxis: displayName, // Use proper display name for xAxis
          impressions: 0,
          clicks: 0,
          mediaCost: 0,
          totalCost: 0,
          ctr: 0,
          cpc: 0,
          cpm: 0,
          ecpm: 0,
          // Preserve other fields from original item
          ...(dataType === 'creatives' && {
            creativeUrl: item.creativeUrl,
            targetUrl: item.targetUrl,
            creativeId: item.creativeId,
            creativeName: item.creativeName,
            s3VideoKey: item.s3VideoKey,
            s3VideoUrl: item.s3VideoUrl,
            activity: activeCreatives.has(item.creativeId) ? 'active' : 'inactive',
          }),
          ...(dataType === 'publishers' && {
            publisherName: item.publisherName,
            publisherIdentifier: item.publisherIdentifier,
            imageUrl: item.imageUrl,
            extra: item.extra,
          }),
        };
      }

      // Aggregate metrics
      aggregatedData[key].impressions += item.impressions || 0;
      aggregatedData[key].clicks += item.clicks || 0;
      aggregatedData[key].mediaCost += item.mediaCost || 0;
      aggregatedData[key].totalCost += item.totalCost || 0;

      // Update to most recent date if item has a more recent date
      if (
        item.date &&
        (!aggregatedData[key].date || moment(item.date).isAfter(moment(aggregatedData[key].date)))
      ) {
        aggregatedData[key].date = item.date;
      }

      // Recalculate CTR after aggregation
      if (aggregatedData[key].impressions > 0) {
        aggregatedData[key].ctr =
          (aggregatedData[key].clicks / aggregatedData[key].impressions) * 100;
      }
    });

    // Sort by impressions (highest first) to match original behavior
    return Object.values(aggregatedData).sort((a, b) => b.impressions - a.impressions);
  };

  // Get filtered data based on date range selection
  const getFilteredCreativeData = () => {
    if (!creativesWithDates || !byCampaignRangeDate || byCampaignRangeDate.length === 0) {
      // Add default 'active' activity status
      if (Array.isArray(creatives)) {
        return creatives.map((creative) => ({ ...creative, activity: 'active' }));
      }
      return creatives;
    }
    return filterAndAggregateData(
      creativesWithDates,
      byCampaignRangeDate[0],
      byCampaignRangeDate[1],
      'creatives'
    );
  };

  const getFilteredPublisherData = () => {
    if (!publishersWithDates || !byCampaignRangeDate || byCampaignRangeDate.length === 0) {
      return publishers;
    }

    const filtered = filterAndAggregateData(
      publishersWithDates,
      byCampaignRangeDate[0],
      byCampaignRangeDate[1],
      'publishers'
    );

    return filtered;
  };

  const marks = {
    1: <>{firstNodeCreatedAt}</>,
  };

  marks[aggregate && aggregate.count] = <div style={{ width: 160 }}>{lastNodeCreatedAt}</div>;

  const TabPane = Tabs.TabPane;
  const emptyCharts = isEmpty(byDate) && isEmpty(byDay);
  const operations = (
    <div style={{ width: '100%' }}>
      <Row align="middle" justify="end">
        <Col flex="160px">
          <Row justify="end">
            <Col>
              <span style={{ fontSize: 10 }}>
                {chartsBuilt && !chartsLoading ? (
                  <TooltipText text={`Updated ${moment(chartsBuilt).calendar()}`} />
                ) : (
                  <Spin />
                )}
              </span>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );

  // Date selector start date and end date for campaign impressions
  const DateSelector = ({ data }) => {
    // map xAxis dates
    const getDate = data.map((a) => a.xAxis);

    // sort them by desc dates
    getDate.sort(function (a, b) {
      return moment(a.date) - moment(b.date);
    });

    // get startdate and end date
    const startDate = getDate[0];
    const endDate = getDate.slice(-1)[0];

    // combined start date & end date with timezone awareness
    const startEndDate = effectiveTimezone === 'UTC'
      ? [moment.utc(startDate), moment.utc(endDate)]
      : [moment.tz(startDate, effectiveTimezone), moment.tz(endDate, effectiveTimezone)];

    let displayRangeDate = byCampaignRangeDate;
    if (!isEmpty(byCampaignRangeDate)) {
      displayRangeDate = effectiveTimezone === 'UTC'
        ? [moment.utc(byCampaignRangeDate[0]), moment.utc(byCampaignRangeDate[1])]
        : [moment.tz(byCampaignRangeDate[0], effectiveTimezone), moment.tz(byCampaignRangeDate[1], effectiveTimezone)];
    }

    return (
      <RangePicker
        //disabledDate={findBetweenDates}
        defaultPickerValue={startEndDate}
        defaultValue={startEndDate}
        value={isEmpty(byCampaignRangeDate) ? startEndDate : displayRangeDate}
        onChange={(v) => {
          // get selected range dates with timezone awareness
          const getSelectedStartDate = v && v.length > 0
            ? (effectiveTimezone === 'UTC' ? moment.utc(v[0]) : moment.tz(v[0], effectiveTimezone))
            : startEndDate[0];
          const getSelectedEndDate = v && v.length > 0
            ? (effectiveTimezone === 'UTC' ? moment.utc(v[1]) : moment.tz(v[1], effectiveTimezone))
            : startEndDate[1];

          // filter data to get only between range dates
          const getFilteredData = data.filter((obj) => {
            const xAxisTimestamp = moment(obj.xAxis).unix();
            return (
              xAxisTimestamp >= getSelectedStartDate.unix() &&
              xAxisTimestamp <= getSelectedEndDate.unix()
            );
          });

          // set the date range on the selected date in calendar
          setByCampaignRangeDate(v);
          // set the data into filtered ones with the selected range dates
          setDataCampaignRangeDate(getFilteredData);
        }}
      />
    );
  };

  const YAxisSelector = ({ displayDateSelector, displayRightSelector, data }) => {
    const validOptions = ['clicks', 'impressions', 'ctr'];

    const keys = Object.keys(data[0])
      .filter((el) => validOptions.includes(el))
      .map((option) => {
        return option === 'cpc' || option === 'cpm' || option === 'ctr' || option === 'ecpm'
          ? option.toUpperCase()
          : option === 'totalCost'
            ? 'Total Cost'
            : capitalize(option);
      })
      .filter((el) => el !== 'Creativeurl');

    return (
      <Row style={{ marginBottom: '2%' }} type="flex" justify="space-between">
        <Frag>
          <Select
            onSelect={(value) => setLeftY(value)}
            style={{
              width: screens.xs === true ? '100%' : '10em',
              minWidth: '10em',
              marginBottom: screens.xs === true ? '0.4em' : '',
            }}
            defaultValue={leftY}
          >
            {keys
              .filter((op) => (displayRightSelector ? op !== rightY : op !== null))
              .map((option) => {
                return (
                  <Option key={option} value={option}>
                    <MenuItemTextColor text={option} color={'#7dafff'} />
                  </Option>
                );
              })}
          </Select>
          {displayDateSelector ? <DateSelector data={byDate} /> : null}
          {displayRightSelector ? (
            <Select
              onSelect={(value) => {
                setRightY(value);
              }}
              style={{
                width: screens.xs === true ? '100%' : '10em',
                minWidth: '10em',
                marginTop: screens.xs === true ? '0.4em' : '',
                marginBottom: screens.xs === true ? '0.4em' : '',
              }}
              defaultValue={rightY}
            >
              {keys
                .filter((op) => op !== leftY)
                .map((option) => {
                  return (
                    <Option key={option} value={option}>
                      <MenuItemTextColor text={option} color={'#92d5a7'} />
                    </Option>
                  );
                })}
            </Select>
          ) : null}
        </Frag>
      </Row>
    );
  };

  if (chartsLoading)
    return (
      <Card style={{ height: '300px' }}>
        <Row type="flex" justify="center" align="middle" style={{ height: '300px' }}>
          <Spin size="large" />
        </Row>
      </Card>
    );
  return (
    <div>
      <Card bodyStyle={{ padding: 0 }}>
        <Tabs
          tabBarExtraContent={operations}
          destroyInactiveTabPane={true}
          defaultActiveKey="2"
          style={colStyle.cssStyle}
          onChange={(activeKey) => {
            if (activeKey === '4') setLeftY('Impressions');
            if (activeKey !== '4') {
              setLeftY('Impressions');
              setRightY('Clicks');
            }
          }}
        >
          {emptyCharts && (
            <TabPane>
              <Card style={{ height: '300px' }}>
                <Row type="flex" justify="center" align="middle" style={{ height: '300px' }}>
                  <Col>
                    <Empty />
                  </Col>
                </Row>
              </Card>
            </TabPane>
          )}
          {!isEmpty(byDate) ? (
            <TabPane tab={<TabText text={'By Date'} />} key="2">
              <Card bordered={false} bodyStyle={{ padding: 0 }}>
                <Frag>
                  <YAxisSelector
                    displayRightSelector={true}
                    displayDateSelector={true}
                    data={byDate}
                  />
                  <DualAxisArea
                    leftY={isEmpty(leftY) ? 'Impressions' : leftY}
                    rightY={isEmpty(rightY) ? 'Clicks' : rightY}
                    data={
                      !dataCampaignRangeDate && !dataCampaignRangeDate.length
                        ? byDate.sort((a, b) => {
                            if (moment(new Date(a.xAxis)).isBefore(new Date(b.xAxis))) return -1;
                            return 1;
                          })
                        : dataCampaignRangeDate
                    }
                  />
                </Frag>
              </Card>
            </TabPane>
          ) : null}
          {!isEmpty(byDay) ? (
            <TabPane tab={<TabText text={'Day Of Week'} />} key="3">
              <Card bordered={false} bodyStyle={{ padding: 0 }}>
                <Frag>
                  <YAxisSelector
                    displayRightSelector={true}
                    displayDateSelector={false}
                    data={byDay}
                  />
                  <DualAxisArea
                    leftY={isEmpty(leftY) ? 'Impressions' : leftY}
                    rightY={isEmpty(rightY) ? 'Clicks' : rightY}
                    data={byDay}
                    showDayOnly={true}
                  />
                </Frag>
              </Card>
            </TabPane>
          ) : null}
          {!isEmpty(deviceType) ? (
            <TabPane tab={<TabText text={'Device Type'} />} key="4">
              <Card bordered={false} bodyStyle={{ padding: 0 }}>
                <YAxisSelector
                  data={deviceType}
                  displayRightSelector={false}
                  displayDateSelector={false}
                />
                <SingleAxisBarChart
                  leftY={isEmpty(leftY) ? 'Impressions' : leftY}
                  data={deviceType}
                />
              </Card>
            </TabPane>
          ) : null}
        </Tabs>
      </Card>
      {creatives || !isEmpty(creatives) ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 4 }}>
          <Creatives
            colStyle={colStyle}
            selectedCampaign={selectedCampaign}
            creativeData={getFilteredCreativeData()}
            chartsLoading={chartsLoading}
          />
        </div>
      ) : null}
      {hasInsights ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 3 }}>
          <Publishers
            colStyle={colStyle}
            selectedCampaign={selectedCampaign}
            publisherData={getFilteredPublisherData()}
            chartsLoading={chartsLoading}
          />
        </div>
      ) : null}
      {segment && !isEmpty(segments) ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 2 }}>
          <SegmentsDetails
            colStyle={colStyle}
            selectedCampaign={selectedCampaign}
            segmentsData={segments}
            selectedSegmentsDimension={selectedSegmentsDimension}
            setSegmentsDimension={setSegmentsDimension}
          />
        </div>
      ) : null}
      {videoLogs && !isEmpty(videoLogs) ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 1 }}>
          <Card title="Video Completion Rate" bordered={false} bodyStyle={{ padding: 0 }}>
            <VideoCompletionRate chartData={videoLogs} chartsLoading={false} />
          </Card>
        </div>
      ) : null}
      {campaigns && !isEmpty(campaigns) && isTrafficker ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 1 }}>
          <CampaignsCard data={campaigns} />
        </div>
      ) : null}
      {lineItems && !isEmpty(lineItems) && lineItems[0].lineItemName && isTrafficker ? (
        <div style={{ marginTop: 10, position: 'relative', zIndex: 1 }}>
          <LineItemsCard data={lineItems} />
        </div>
      ) : null}
    </div>
  );
};

export default OverviewTab;
